import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkAgeCompliance } from '@4cm/core/auth';
import { ConsentLayout } from './ConsentLayout';
import { setConsentDraft } from '../../../lib/consent/consentDraft';

/**
 * Step 1 — child enters their date of birth.
 *
 * Per parent-consent-ux-spec.md §2 Step 1: this is the first thing
 * 4CM ever stores about the child. No name, no email, no team yet.
 *
 * After submit:
 *   - <13 → Step 2 (parent email)
 *   - 13–17 → /auth/uk-gdpr/teen-onboarding (lighter flow; not built
 *             in Wave 3.2 — for now, redirect to a placeholder)
 *   - 18+ → adult signup (out of scope; redirect to home)
 */
export function DobScreen() {
  const navigate = useNavigate();
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const d = parseInt(day, 10);
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);
    if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) {
      setError('Please enter your date of birth.');
      return;
    }
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1980 || y > new Date().getFullYear()) {
      setError("That doesn't look like a valid date.");
      return;
    }
    const dob = new Date(y, m - 1, d);
    if (Number.isNaN(dob.getTime())) {
      setError("That doesn't look like a valid date.");
      return;
    }
    const status = checkAgeCompliance(dob, 'gb');
    setConsentDraft({ childDob: dob.toISOString().slice(0, 10) });

    switch (status.status) {
      case 'UNDER_13_DEFERRED':
        // V1 launch posture (22a): under-13 player accounts deferred to v2.
        // The parent doesn't need to gate consent if the child has no
        // account. Route to home — the parent will still see coach-of-
        // record notes via the portal, just without a per-child login.
        // TODO(22a): build a proper "your child is under 13 — here's what
        // that means" parent portal page.
        navigate('/');
        break;
      case 'REQUIRES_PARENTAL_CONSENT':
        navigate('/parent/consent/parent-email');
        break;
      case 'INFORMED_PARENTAL_NOTICE':
        // 13–17 lighter flow — Wave 3.2 doesn't build this; placeholder
        // sends them to a "we'll handle this in a future release" page.
        // For now, route to home.
        navigate('/');
        break;
      case 'OK':
        // Adult signup is out of scope for the parent-portal track.
        navigate('/');
        break;
    }
  };

  return (
    <ConsentLayout
      step={1}
      title="When were you born?"
      lead="We need this so we can give you the right experience for your age. It's the first thing 4CM ever stores about you."
    >
      <form className="consent-form" onSubmit={handleSubmit} noValidate>
        <div className="consent-form__field">
          <label className="consent-form__label" htmlFor="dob-day">Date of birth</label>
          <div className="consent-form__row">
            <input
              id="dob-day"
              className={`consent-form__input ${error ? 'consent-form__input--error' : ''}`}
              type="number"
              min={1}
              max={31}
              placeholder="DD"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              autoFocus
              inputMode="numeric"
              aria-label="Day"
            />
            <input
              id="dob-month"
              className={`consent-form__input ${error ? 'consent-form__input--error' : ''}`}
              type="number"
              min={1}
              max={12}
              placeholder="MM"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              inputMode="numeric"
              aria-label="Month"
            />
            <input
              id="dob-year"
              className={`consent-form__input ${error ? 'consent-form__input--error' : ''}`}
              type="number"
              min={1980}
              max={new Date().getFullYear()}
              placeholder="YYYY"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              inputMode="numeric"
              aria-label="Year"
            />
          </div>
          {error && <span className="consent-form__error">{error}</span>}
        </div>

        <div className="consent-form__actions">
          <button type="submit" className="consent-form__btn">Continue</button>
        </div>
      </form>
    </ConsentLayout>
  );
}
